import { Typography } from '@breetometer/components';
import { CopyIcon, FullStopIcon, SearchIcon } from 'BreetAssets';
import { DropdownButton, EmptyState, SearchInput, TableTooltip } from 'BreetComponents';
import { copyToClipboard } from 'BreetHelpers';
import debounce from 'lodash/debounce';
import { type ChangeEvent, type MouseEvent, type ReactNode, useCallback, useMemo, useState } from 'react';

import { getRowKey, getValueByPath, type ITable, type ITableColumn } from './Table.helpers';
import TableBodyLoader from './TableBodyLoader';

const Table = <E extends { _id?: string } | { id?: string }>({
	columns,
	isLoading,
	data,
	hideHeader,
	isSearchFilter,
	skeletonRows = 5,
}: ITable<E>) => {
	const [searchObject, setSearchObject] = useState<Record<string, string> | undefined>();
	const debouncedSearch = useMemo(
		() =>
			debounce((onSearch: (data: Record<string, string>) => void, searchKey: string | number, value: string) => {
				onSearch({ [String(searchKey)]: value });
			}, 300),
		[]
	);
	const handleTableColumnSearch = useCallback(
		(onSearch: (data: Record<string, string>) => void, searchKey: string | number) => (e: ChangeEvent<HTMLInputElement>) => {
			setSearchObject({ [String(searchKey)]: e.target.value });
			debouncedSearch(onSearch, searchKey, e.target.value);
		},
		[debouncedSearch]
	);

	const handleTableColumnClick = useCallback(
		(
			{ row, column }: { row: E; column: ITableColumn<E> },
			onClick?: (props: { row: E; column: ITableColumn<E> }) => Promise<void> | void
		) =>
			async () => {
				await onClick?.({ row, column });
			},
		[]
	);

	const handleCopy = useCallback(
		(value: string, message?: string) => async (e: MouseEvent<SVGSVGElement>) => {
			e.stopPropagation();
			await copyToClipboard(value, message)();
		},
		[]
	);

	const searchIconClass = (key: string) => `table-header-search-button_searchicon ${searchObject?.[key] ? 'active' : ''}`;

	const isDataEmpty = !data?.length && !isSearchFilter;

	const isFetching = !data?.length && isLoading;

	const visibleColumns = columns.filter((column) => !column.hideColumn);

	return (
		<div className={`tablecontainer scrollbar ${!data?.length && !isLoading ? 'no-data' : ''}`}>
			<table>
				<thead
					style={{ display: hideHeader ? 'none' : '' }}
					className='bg-primary-400 color-neutral-50'
				>
					{isFetching ? (
						<tr>
							{Array.from({ length: visibleColumns.length }).map((_, index) => (
								<th key={`table-plain-head-cell-skeleton-${String(index)}`}>
									<div
										className='global-skeleton'
										style={{ width: '100%' }}
									>
										loading...
									</div>
								</th>
							))}
						</tr>
					) : (
						<tr>
							{columns
								.filter((i) => !i.hideColumn)
								.map((column, columnKey) => (
									<th
										key={`table-head-cell-${column.key}-${columnKey} color-neutral-50 bg-primary-400`}
										style={{ width: data?.length ? column.width : undefined, minWidth: data?.length ? column.width : undefined }}
									>
										{column.onSearch ? (
											<div className='table-header-search-button'>
												<DropdownButton
													label={column.title as string}
													trailingIcon={
														<div className={searchIconClass(column.searchKey ?? column.key)}>
															<SearchIcon variant='default' />
															{!!searchObject?.[column.searchKey ?? column.key] && <FullStopIcon />}
														</div>
													}
													variant='primary'
													placement='left'
													dropDownComponent={
														<div className='appfilter dropdownshadow'>
															<SearchInput
																widthType='full'
																id='table-search'
																onChange={handleTableColumnSearch(column.onSearch, column.searchKey ?? column.key)}
																placeholder={`Search ${column.title as string}`}
																// eslint-disable-next-line jsx-a11y/no-autofocus
																autoFocus
																value={searchObject?.[column.searchKey ?? column.key]}
															/>
														</div>
													}
												/>
											</div>
										) : (
											column.title
										)}
									</th>
								))}
						</tr>
					)}
				</thead>

				{isFetching ? (
					<TableBodyLoader
						skeletonRows={skeletonRows}
						skeletonColumns={visibleColumns.length}
					/>
				) : (
					<>
						{data?.length ? (
							<tbody>
								{data.map((dataItem) => (
									<tr
										key={`table-body-${getRowKey(dataItem)}`}
										className='bg-primary-500-hover'
									>
										{columns
											.filter((i) => !i.hideColumn)
											.map((column, columnIndex) => {
												const itemValue = getValueByPath(dataItem, column.key);
												const renderedValue = column.render ? column.render({ row: dataItem, column }) : (itemValue as ReactNode);
												return (
													<td
														key={`table-row-cell-${column.key}-${columnIndex}`}
														style={{ width: column.width, minWidth: column.width }}
														className='color-neutral-50 cursor_pointer'
														onClick={handleTableColumnClick({ row: dataItem, column }, column.onClick)}
													>
														<TableTooltip enabled={!!column.onClick}>
															{column.copy && typeof renderedValue === 'string' ? (
																<div className='table-cell-div copy'>
																	{column.copyIconPosition !== 'after' && (
																		<CopyIcon
																			variant='plain'
																			focusable
																			onClick={handleCopy(renderedValue, column.copyMessage)}
																			className='cursor_pointer svg-dark'
																		/>
																	)}
																	<Typography variant='paragraph_2'>{renderedValue}</Typography>
																	{column.copyIconPosition === 'after' && (
																		<CopyIcon
																			variant='plain'
																			focusable
																			onClick={handleCopy(renderedValue, column.copyMessage)}
																			className='cursor_pointer svg-dark'
																		/>
																	)}
																</div>
															) : (
																renderedValue
															)}
														</TableTooltip>
													</td>
												);
											})}
									</tr>
								))}
							</tbody>
						) : null}
					</>
				)}
			</table>
			{!data?.length && !isLoading && (
				<EmptyState
					searchNotFound={isSearchFilter}
					title={isDataEmpty ? 'No records found' : ''}
					description={isDataEmpty ? 'Data will appear here when available.' : ''}
					isLoading={isLoading}
				/>
			)}
		</div>
	);
};

export default Table;
